import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { Icon } from "@iconify/react";
import {
  getReasonTypeLabelParts,
  groupByReasonCode
} from "../utils/rfqReasonUtils";

const RFQReasonsModal = ({ items, onPartClick }) => {
  const totalItems = items?.length || 0;

  const [expandedReasons, setExpandedReasons] = useState(new Set());

  const toggleExpand = (reasonKey) => {
    setExpandedReasons(prev => {
      const next = new Set(prev);
      if (next.has(reasonKey)) {
        next.delete(reasonKey);
      } else {
        next.add(reasonKey);
      }
      return next;
    });
  };

  const isExpanded = (reasonKey) => expandedReasons.has(reasonKey);

  // No items to display
  if (totalItems === 0) {
    return <p>No RFQ details available.</p>;
  }

  const groupedReasons = groupByReasonCode(items);

  return (
    <div className="rfq-reasons-panel">
      <p className="mb-3">The following items require manual review:</p>
      <div className="rfq-reasons-table" style={{ borderTop: '1px solid #e9ecef' }}>
        {Object.entries(groupedReasons).map(([reasonCode, affectedItems]) => {
          const reasonKey = reasonCode;
          const expanded = isExpanded(reasonKey);

          // Extract limit from the first item's reason (all items in this group have the same limit)
          const firstItemReason = affectedItems[0]?.reason;
          const limit = firstItemReason?.limit || null;

          const labelParts = getReasonTypeLabelParts(reasonCode, limit);

          return (
            <div key={reasonKey} className="rfq-table-item" style={{ borderBottom: '1px solid #e9ecef' }}>
              <div
                className={`rfq-table-cell-name p-3 d-flex align-items-start justify-content-between ${expanded ? 'bg-light' : ''}`}
                onClick={() => toggleExpand(reasonKey)}
                style={{
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!expanded) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!expanded) {
                    e.currentTarget.style.backgroundColor = '';
                  }
                }}
              >
                <div className="d-flex align-items-start flex-grow-1">
                  <Icon
                    icon="mdi:chevron-down"
                    style={{
                      fontSize: '20px',
                      color: '#6c757d',
                      transition: 'transform 0.2s ease',
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      marginRight: '8px',
                      marginTop: '2px',
                      flexShrink: 0
                    }}
                  />
                  <div className="flex-grow-1">
                    <div className="fw-bold mb-1">
                      {labelParts.main}
                      {labelParts.threshold && (
                        <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '6px' }}>
                          ({labelParts.threshold})
                        </span>
                      )}
                    </div>
                    <div className="small" style={{ color: '#6c757d' }}>
                      {affectedItems.length} part{affectedItems.length !== 1 ? 's' : ''} affected
                    </div>
                  </div>
                </div>
              </div>
              {expanded && (
                <div className="rfq-table-cell-reasons p-3 bg-white" style={{ borderTop: '1px solid #e9ecef' }}>
                  <ul className="mb-0" style={{ listStyle: 'none', paddingLeft: '0' }}>
                    {affectedItems.map((affectedItem, idx) => {
                      // part_code now contains the stable identifier (last 3 segments) from backend
                      const stableIdentifier = affectedItem.part_code || 'N/A';
                      const isClickable = onPartClick && affectedItem.canFocus === true;

                      const handlePartClick = (e) => {
                        e.stopPropagation();
                        if (isClickable) {
                          onPartClick({
                            itemId: affectedItem.item_id,
                            stableIdentifier,
                            itemName: affectedItem.item_name,
                            item: affectedItem
                          });
                        }
                      };

                      return (
                        <li key={idx} className="mb-3 d-flex align-items-start">
                          <span className="me-2" style={{ color: '#6c757d' }}>•</span>
                          <div className="flex-grow-1 d-flex align-items-start justify-content-between">
                            <div className="flex-grow-1">
                              <div className="fw-bold mb-1">{affectedItem.item_name}</div>
                              {stableIdentifier !== 'N/A' && (
                                <div className="small" style={{ color: '#3c5b8a', fontWeight: '500' }}>
                                  {stableIdentifier}
                                </div>
                              )}
                            </div>
                            {isClickable && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handlePartClick}
                                title="Focus on this part in the order"
                                style={{ marginLeft: '8px' }}
                              >
                                Focus
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RFQReasonsModal;
